import { useEffect, useState } from "react";

/**
 * Breakpoint hook — vrací aktuální viewport bucket per Tailwind 4.2 defaults.
 *
 * Buckets:
 *   - 'mobile'  : < 768 px   (Tailwind `md` threshold)
 *   - 'tablet'  : 768–1023 px (md ≤ w < lg)
 *   - 'desktop' : ≥ 1024 px  (lg+)
 *
 * SSR-safe (initial = 'desktop' když window není dostupné).
 */
export type Breakpoint = "mobile" | "tablet" | "desktop";

export const BREAKPOINTS = {
  md: 768,
  lg: 1024,
} as const;

export function computeBreakpoint(width: number): Breakpoint {
  if (width < BREAKPOINTS.md) return "mobile";
  if (width < BREAKPOINTS.lg) return "tablet";
  return "desktop";
}

function readBreakpoint(): Breakpoint {
  if (typeof window === "undefined") return "desktop";
  return computeBreakpoint(window.innerWidth);
}

export function useBreakpoint(): Breakpoint {
  const [bp, setBp] = useState<Breakpoint>(() => readBreakpoint());

  useEffect(() => {
    if (typeof window === "undefined") return;
    const onResize = () => setBp(computeBreakpoint(window.innerWidth));
    window.addEventListener("resize", onResize);
    onResize();
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return bp;
}
